import { useEffect } from "react";
import { ArrowLeft, Star, Instagram } from "lucide-react";
import { IMAGES } from "@/config/site";

const CREDENTIALS = [
  { label: "Office", value: "Managing Director — Sports Science & Analytics" },
  { label: "Hometown", value: "Henderson, Nevada" },
  { label: "Graduate study", value: "M.S. Sports Science & Analytics — Linfield University (first-year graduate student)" },
  { label: "Undergraduate", value: "Human Growth & Development — University of Providence (MT)" },
];

const FILMS = [
  {
    url: "/assets/directors/jalen_film_1.mp4",
    poster: "/assets/directors/jalen_poster_1.jpg",
    caption: "Field session — sprint mechanics and plyometric preparation",
    wide: true,
  },
  {
    url: "/assets/directors/jalen_film_2.mp4",
    poster: "/assets/directors/jalen_poster_2.jpg",
    caption: "Pro athlete off-season training — Coach Shepard with Kitan Oladapo, Green Bay Packers safety",
    wide: false,
  },
  {
    url: "/assets/directors/jalen_film_5.mp4",
    poster: "/assets/directors/jalen_poster_5.jpg",
    caption: "Boxing conditioning — wraps, pads, and footwork in the room",
    wide: false,
  },
  {
    url: "/assets/directors/jalen_film_3.mp4",
    poster: "/assets/directors/jalen_poster_3.jpg",
    caption: "MVRK Ball Club — youth movement preparation",
    wide: true,
  },
  {
    url: "/assets/directors/jalen_film_4.mp4",
    poster: "/assets/directors/jalen_poster_4.jpg",
    caption: "MVRK Ball Club — plyometric work with young athletes",
    wide: false,
  },
];

const TESTIMONIALS = [
  {
    name: "Erin H.",
    quote: "Second to none! Every time I get a workout from him, my body feels stronger as well as more fluid.",
  },
  {
    name: "Ellen C.",
    quote: "Very thoughtful about his training plans… calm and kind and focused and very effective.",
  },
  {
    name: "Donald Morris",
    quote: "The absolute best… genuinely cares about improving your quality of life. Extremely knowledgeable about the mind-body connection.",
  },
  {
    name: "Matt Clark",
    quote: "A fantastic coach, great collaborator and expert in his field… thoughtful training grounded in hard work and effort. Highly recommended.",
  },
  {
    name: "Tramy S.",
    quote: "Amazing at giving you a personalized experience in a group setting.",
  },
  {
    name: "Shawn G.",
    quote: "An impeccable ability to adjust his guidance to the needs of the individual… his understanding of the person always shines through.",
  },
];

const GALLERY = [
  {
    url: "/assets/directors/jalen-training.jpg",
    alt: "Jalen Shepard coaching two young athletes through on-court basketball skill work",
    caption: "On-court skill work — movement quality before load",
    anchor: "object-top",
  },
  {
    url: "/assets/directors/jalen-meadow-park.jpg",
    alt: "Jalen Shepard with the Meadow Park youth basketball program",
    caption: "With the Meadow Park youth basketball program",
  },
  {
    url: "/assets/directors/jalen-nsca-conference.jpg",
    alt: "Linfield Sports Science and Analytics graduate students at the 2024 NSCA Northwest Regional Conference",
    caption: "2024 NSCA Northwest Regional Conference — Nike World Headquarters",
  },
  {
    url: "/assets/directors/jalen-linfield-cohort.jpg",
    alt: "Jalen Shepard with his Linfield University Sports Science and Analytics graduate cohort",
    caption: "The Linfield Sports Science & Analytics graduate cohort",
  },
  {
    url: "/assets/directors/jalen-ladder-session.jpg",
    alt: "Graduate cohort running speed-ladder footwork drills during a movement session",
    caption: "Speed-ladder footwork — movement session with the graduate cohort",
  },
];

// floating-banner positions for the Wall of Proof (lg screens); delays stagger the drift
const FLOAT_SLOTS = [
  { style: { top: "4%", left: "0%" }, delay: "0s", dur: "7s", rot: "-1.5deg" },
  { style: { top: "0%", right: "2%" }, delay: "1.2s", dur: "8s", rot: "1.2deg" },
  { style: { top: "38%", left: "2%" }, delay: "0.6s", dur: "9s", rot: "1deg" },
  { style: { top: "42%", right: "0%" }, delay: "1.8s", dur: "7.5s", rot: "-1deg" },
  { style: { bottom: "2%", left: "30%" }, delay: "0.9s", dur: "8.5s", rot: "0.8deg" },
  { style: { bottom: "6%", right: "4%" }, delay: "2.2s", dur: "7.8s", rot: "-1.2deg" },
];

const Stars = () => (
  <div className="flex gap-0.5" aria-label="Five star review">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={12} className="fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const ReviewCard = ({ t }) => (
  <div className="rounded-xl border border-stone-700/60 bg-stone-900/85 backdrop-blur-md p-4 space-y-2 shadow-[0_10px_40px_rgba(0,0,0,0.45)]">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-bold text-stone-100">{t.name}</p>
      <Stars />
    </div>
    <p className="text-xs text-stone-300 leading-relaxed">"{t.quote}"</p>
  </div>
);

const PROGRAMS = [
  { title: "Somatic Strength 1-on-1", copy: "Private sessions built on body awareness — owned positions, controlled tempo, strength that starts in the nervous system.", tag: "Private" },
  { title: "Boxing Conditioning", copy: "Wraps, pads, footwork, and rounds — fight-craft conditioning for fitness and coordination, no ring required.", tag: "All levels" },
  { title: "Speed & Plyometric Prep", copy: "Sprint mechanics, bounding, and elastic power for field and court athletes — speed earned through position.", tag: "Athletes" },
  { title: "Youth Development", copy: "Long-term athletic growth for young movers — coordination, skill, and love of the work before specialization.", tag: "Youth" },
  { title: "Pro Off-Season", copy: "Structured off-season blocks for professionals — the same room where pro defensive backs prepare.", tag: "Elite" },
  { title: "Recovery & Wellness Programming", copy: "The full-life regimen: training blocks, recovery days, sleep, and schedule — wellness that compounds.", tag: "Everyone" },
];

const MEMBERSHIPS = [
  {
    name: "The Floor",
    blurb: "Open programming — the desk's monthly training plan, run on your own floor.",
    points: ["Monthly programmed training blocks", "Movement library access", "Quarterly check-in"],
  },
  {
    name: "The Craft",
    blurb: "Coached membership — programming plus hands-on sessions with Director Shepard.",
    points: ["Everything in The Floor", "Weekly coached sessions", "Movement assessment each block", "Recovery & schedule guidance"],
    featured: true,
  },
  {
    name: "The Table",
    blurb: "Full preparation for competitive and professional athletes.",
    points: ["Everything in The Craft", "Full off-season / in-season design", "Performance testing & analytics", "Priority scheduling"],
  },
];

const STEPS = [
  { n: "01", title: "Assessment", copy: "A movement screen and a conversation — how you move, where you're headed, what the body is telling us." },
  { n: "02", title: "Program", copy: "Your block is written for your body and your calendar — strength, speed, recovery, all of it scheduled." },
  { n: "03", title: "Track", copy: "Honest numbers each block. The measurement serves the mover — the program adjusts, the progress compounds." },
];

const DESK_SMS = "sms:+17023785659?&body=Coach%20Jalen%20—%20claiming%20a%20training%20slot.%20My%20goal%3A%20";

// Setmore live-scheduler link — activates the booking embed the moment it's set
const SETMORE_URL = "https://somaticstrength.setmore.com";

const DirectorJalen = () => {
  // Load Setmore's live-scheduler widget once the booking link is set
  useEffect(() => {
    if (!SETMORE_URL) return;
    if (document.getElementById("setmore_script")) return;
    const s = document.createElement("script");
    s.id = "setmore_script";
    s.src = "https://assets.setmore.com/integration/static/setmoreIframeLive.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <style>{`
        @keyframes jalenFloat {
          0%, 100% { transform: translateY(0) rotate(var(--rot, 0deg)); }
          50% { transform: translateY(-12px) rotate(var(--rot, 0deg)); }
        }
        .jalen-float { animation: jalenFloat var(--dur, 8s) ease-in-out infinite; animation-delay: var(--delay, 0s); }
        @keyframes jalenTraverseX {
          0% { transform: translateX(-6%) scale(1.18); }
          50% { transform: translateX(6%) scale(1.28); }
          100% { transform: translateX(-6%) scale(1.18); }
        }
        @keyframes jalenTraverseY {
          0% { transform: translateY(-5%) scale(1.12); }
          50% { transform: translateY(5%) scale(1.22); }
          100% { transform: translateY(-5%) scale(1.12); }
        }
        .jalen-traverse-x { animation: jalenTraverseX 90s ease-in-out infinite; }
        .jalen-traverse-y { animation: jalenTraverseY 110s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .jalen-traverse-x, .jalen-traverse-y, .jalen-float { animation: none; }
        }
      `}</style>

      {/* traversing background — the work, always in motion behind the page */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <img
          src="/assets/directors/jalen-plaid-wellness.jpg"
          alt=""
          className="jalen-traverse-x absolute -top-[14%] left-[-6%] w-[128%] max-w-none opacity-[0.11] saturate-[0.55] brightness-[0.72]"
        />
        <img
          src="/assets/directors/jalen-gym-backdrop.jpg"
          alt=""
          className="jalen-traverse-y absolute bottom-[-16%] right-[-10%] h-[120%] max-w-none opacity-[0.10] saturate-[0.6] brightness-[0.7]"
        />
        <img
          src="/assets/directors/jalen-training.jpg"
          alt=""
          className="jalen-traverse-x absolute top-[36%] left-[6%] w-[92%] max-w-none opacity-[0.06] saturate-[0.5] brightness-[0.66]"
        />
        <div className="absolute inset-0 bg-stone-950/78" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12 space-y-16">
        <a
          href="/#directors"
          data-testid="jalen-back-link"
          className="inline-flex items-center gap-2 text-xs text-stone-400 hover:text-amber-400 transition-colors"
        >
          <ArrowLeft size={14} /> Back to Directorship
        </a>

        {/* Hero — the man and the office */}
        <div className="grid md:grid-cols-5 gap-10 items-start">
          <div
            className="md:col-span-2 rounded-xl overflow-hidden border border-amber-500/30 shadow-[0_0_50px_rgba(245,158,11,0.10)]"
            data-testid="jalen-portrait"
          >
            <img
              src={IMAGES.jalenShepard.url}
              alt={IMAGES.jalenShepard.alt}
              className="w-full h-[420px] object-cover object-[50%_22%]"
            />
          </div>

          <div className="md:col-span-3 space-y-8">
            <div className="space-y-3">
              <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
                Managing Directorship
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight display-serif">
                Jalen Shepard
              </h1>
              <p className="text-stone-300 text-base md:text-lg">
                Sports Science &amp; Analytics — the craft of the human body
              </p>
              <a
                href="https://instagram.com/peace_of_jo"
                target="_blank"
                rel="noreferrer"
                data-testid="jalen-instagram-link"
                className="inline-flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 transition-colors"
              >
                <Instagram size={14} /> @peace_of_jo — follow the craft
              </a>
              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href={DESK_SMS}
                  data-testid="jalen-claim-slot-button"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-opacity hover:opacity-85"
                  style={{ background: "#ecd9b8", color: "#12100c" }}
                >
                  Claim a training slot
                </a>
                <a
                  href="#jalen-memberships"
                  data-testid="jalen-memberships-link"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-stone-700 text-stone-200 hover:border-amber-500/60 hover:text-amber-300 transition-colors"
                >
                  View memberships
                </a>
              </div>
            </div>

            <blockquote
              data-testid="jalen-quote"
              className="border-l-2 border-amber-500/60 pl-5 py-1 text-stone-200 text-base md:text-lg italic display-serif"
            >
              "I am looking forward to the trial by fire."
            </blockquote>

            <div className="space-y-4" data-testid="jalen-credentials">
              {CREDENTIALS.map((row) => (
                <div key={row.label} className="border-b border-stone-800/70 pb-3">
                  <p className="mono-tag text-[10px] uppercase tracking-widest text-stone-500">
                    {row.label}
                  </p>
                  <p className="text-sm text-stone-200 mt-1">{row.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* In-motion banner — the membership film, full width, always moving */}
        <section
          data-testid="jalen-motion-banner"
          className="relative rounded-xl overflow-hidden border border-amber-500/25 shadow-[0_0_60px_rgba(245,158,11,0.10)]"
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full aspect-video object-cover"
          >
            <source src="/assets/directors/jalen-gem-membership-wide.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-stone-950/30 pointer-events-none" />
          <div className="absolute bottom-4 left-5">
            <span className="mono-tag text-[10px] uppercase tracking-widest text-amber-400/90">
              Somatic Strength & Wellness — in motion
            </span>
          </div>
        </section>

        {/* Film Room — motion first; athletes learn by watching */}
        <section className="space-y-6" data-testid="jalen-film-room">
          <div className="max-w-2xl space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Film Room
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
              The Work, in Motion
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            {FILMS.map((film, i) => (
              <figure
                key={film.url}
                data-testid={`jalen-film-${i}`}
                className="rounded-xl overflow-hidden bg-stone-900/60 border border-stone-800"
              >
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster={film.poster}
                  className={film.wide ? "w-full aspect-video object-cover" : "w-full max-h-[480px] object-contain bg-black"}
                >
                  <source src={film.url} type="video/mp4" />
                </video>
                <figcaption className="px-4 py-3 text-xs text-stone-400">{film.caption}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Wall of Proof — floating five-star banners around the central claim */}
        <section className="space-y-6" data-testid="jalen-proof-wall">
          <div className="max-w-2xl space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              The Proof
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
              In the Athletes' Own Words
            </h2>
          </div>

          {/* floating layout — large screens */}
          <div className="relative hidden lg:block min-h-[640px]" aria-hidden={false}>
            <div className="absolute inset-0 flex items-center justify-center text-center px-24">
              <div className="space-y-3">
                <p className="display-serif text-3xl font-bold text-stone-100 leading-snug">
                  Natural movement.<br />Real strength.<br />Proven on bodies.
                </p>
                <p className="mono-tag text-[10px] uppercase tracking-widest text-stone-500">
                  Five-star reviews from the athletes he trains
                </p>
              </div>
            </div>
            {TESTIMONIALS.map((t, i) => (
              <div
                key={t.name}
                data-testid={`jalen-review-${i}`}
                className="jalen-float absolute w-72"
                style={{
                  ...FLOAT_SLOTS[i].style,
                  "--delay": FLOAT_SLOTS[i].delay,
                  "--dur": FLOAT_SLOTS[i].dur,
                  "--rot": FLOAT_SLOTS[i].rot,
                }}
              >
                <ReviewCard t={t} />
              </div>
            ))}
          </div>

          {/* stacked layout — small screens */}
          <div className="grid sm:grid-cols-2 gap-4 lg:hidden">
            {TESTIMONIALS.map((t, i) => (
              <ReviewCard key={t.name} t={t} />
            ))}
          </div>
        </section>

        {/* The Work — somatic strength & wellness */}
        <section className="space-y-6" data-testid="jalen-work-section">
          <div className="max-w-2xl space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              The Work
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
              Athletic Movement through Somatic Strength &amp; Wellness
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 text-sm text-stone-300 leading-relaxed">
            <p>
              Director Shepard's practice starts from a simple premise: an athlete is a body that
              feels before it is a body that performs. Somatic strength — strength built on
              body awareness, controlled positions, and deliberate movement — comes before
              barbell numbers and stopwatch times. On the court that means teaching young
              athletes to own their stance, their balance, and their breath before asking for
              speed.
            </p>
            <p>
              Wellness completes the arc: recovery, daily regimen, and the habits of growth he
              studied in Human Growth &amp; Development at the University of Providence and now
              sharpens through graduate work in Sports Science &amp; Analytics at Linfield
              University — measurement in service of the mover, never the other way around.
              From Meadow Park's youth program to continued education with the NSCA, his
              directorship carries the Institute's athletics pillar into lived practice.
            </p>
          </div>
        </section>

        {/* Wellness — his signature regimen philosophy */}
        <section className="space-y-6" data-testid="jalen-wellness-feature">
          <div className="grid md:grid-cols-5 gap-8 items-center rounded-xl bg-stone-900/60 border border-stone-800 overflow-hidden">
            <div className="md:col-span-2 h-full">
              <img
                src="/assets/directors/jalen-wellness-beach.webp"
                alt="Silhouette training on the beach at sunset — Wellness begins with a good schedule, @peace_of_jo"
                className="w-full h-full object-cover max-h-[520px]"
                loading="lazy"
              />
            </div>
            <div className="md:col-span-3 p-6 md:p-8 space-y-4">
              <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
                The Regimen
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
                Wellness Begins with a Good Schedule
              </h2>
              <p className="text-sm text-stone-300 leading-relaxed">
                Strength is built in the room, but wellness is kept on the calendar. Director
                Shepard's athletes learn to schedule the whole life of the body — training
                blocks, recovery days, sleep, meals, sunlight — so progress compounds instead
                of colliding. The schedule is not a cage; it is the rhythm that lets natural
                movement stay natural.
              </p>
              <a
                href="https://instagram.com/peace_of_jo"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 transition-colors"
              >
                <Instagram size={14} /> @peace_of_jo
              </a>
            </div>
          </div>
        </section>

        {/* Field gallery */}
        <section className="space-y-6" data-testid="jalen-gallery">
          <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
            In the Field
          </span>
          <div className="grid sm:grid-cols-2 gap-6">
            {GALLERY.map((shot) => (
              <figure
                key={shot.url}
                className="rounded-xl overflow-hidden bg-stone-900/60 border border-stone-800 group"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={shot.url}
                    alt={shot.alt}
                    className={`w-full h-full object-cover ${shot.anchor || "object-center"} group-hover:scale-105 transition-transform duration-700`}
                    loading="lazy"
                  />
                </div>
                <figcaption className="px-4 py-3 text-xs text-stone-400">{shot.caption}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Programs — the gym floor's offer */}
        <section className="space-y-6" data-testid="jalen-programs">
          <div className="max-w-2xl space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Programs
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
              Specialists in the Craft of the Human Body
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROGRAMS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`jalen-program-${i}`}
                className="p-5 rounded-xl bg-stone-900/60 border border-stone-800 space-y-2 hover:border-amber-600/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-bold text-stone-100">{p.title}</h3>
                  <span className="mono-tag text-[9px] uppercase tracking-widest text-amber-500 border border-amber-500/30 rounded-full px-2 py-0.5 whitespace-nowrap">
                    {p.tag}
                  </span>
                </div>
                <p className="text-xs text-stone-400 leading-relaxed">{p.copy}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Memberships */}
        <section className="space-y-6" id="jalen-memberships" data-testid="jalen-memberships">
          <div className="max-w-2xl space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Membership
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
              Take Your Seat at the Desk
            </h2>
            <p className="text-xs text-stone-400">
              Rates are set at your assessment — every body and every calendar prices differently.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 items-stretch">
            {MEMBERSHIPS.map((m, i) => (
              <div
                key={m.name}
                data-testid={`jalen-membership-${i}`}
                className={`rounded-xl p-6 flex flex-col space-y-4 border ${
                  m.featured
                    ? "border-amber-500/50 bg-stone-900 shadow-[0_0_45px_rgba(245,158,11,0.10)]"
                    : "border-stone-800 bg-stone-900/60"
                }`}
              >
                <div className="space-y-1">
                  {m.featured && (
                    <span className="mono-tag text-[9px] uppercase tracking-widest text-amber-500 block">
                      Most Claimed
                    </span>
                  )}
                  <h3 className="text-xl font-bold display-serif text-stone-100">{m.name}</h3>
                  <p className="text-xs text-stone-400 leading-relaxed">{m.blurb}</p>
                </div>
                <ul className="space-y-2 flex-1">
                  {m.points.map((pt) => (
                    <li key={pt} className="text-xs text-stone-300 flex gap-2">
                      <span className="text-amber-500 mt-0.5">—</span> {pt}
                    </li>
                  ))}
                </ul>
                <a
                  href={DESK_SMS}
                  data-testid={`jalen-membership-cta-${i}`}
                  className={`block text-center rounded-full py-2.5 text-sm font-semibold transition-opacity hover:opacity-85 ${
                    m.featured ? "" : "border border-stone-700 text-stone-200 hover:border-amber-500/60"
                  }`}
                  style={m.featured ? { background: "#ecd9b8", color: "#12100c" } : undefined}
                >
                  Text the desk
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Booking — Setmore live scheduler space */}
        <section className="space-y-6" data-testid="jalen-booking">
          <div className="rounded-xl border border-amber-500/40 bg-stone-900 shadow-[0_0_45px_rgba(245,158,11,0.08)] p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div className="space-y-3">
                <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
                  Book the Floor
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
                  Reserve Your Session
                </h2>
                <p className="text-xs text-stone-400 leading-relaxed">
                  The desk books through Setmore — real-time availability, instant confirmation,
                  reminders before every session. Pick your slot and it's yours.
                </p>
              </div>
              <div className="flex flex-col items-center gap-3">
                {SETMORE_URL ? (
                  <>
                    <a
                      id="Setmore_button_iframe"
                      data-testid="jalen-setmore-button"
                      href={SETMORE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full px-8 py-3.5 text-sm font-semibold transition-opacity hover:opacity-85"
                      style={{ background: "#ecd9b8", color: "#12100c" }}
                    >
                      Book with Setmore
                    </a>
                  </>
                ) : (
                  <a
                    href={DESK_SMS}
                    data-testid="jalen-booking-sms-fallback"
                    className="rounded-full px-8 py-3.5 text-sm font-semibold transition-opacity hover:opacity-85"
                    style={{ background: "#ecd9b8", color: "#12100c" }}
                  >
                    Text the desk to book
                  </a>
                )}
                <p className="text-[10px] text-stone-500">
                  Sessions confirmed directly with Director Shepard
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="space-y-6" data-testid="jalen-steps">
          <div className="max-w-2xl space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              The Path
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
              Assessment. Program. Track.
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="p-5 rounded-xl bg-stone-900/60 border border-stone-800 space-y-2">
                <span className="mono-tag text-xs text-amber-500">{s.n}</span>
                <h3 className="text-sm font-bold text-stone-100">{s.title}</h3>
                <p className="text-xs text-stone-400 leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </section>

        {/* The desk */}
        <section
          className="rounded-xl bg-stone-900/60 border border-stone-800 p-6 space-y-4"
          data-testid="jalen-desk-note"
        >
          <p className="mono-tag text-[10px] uppercase tracking-widest text-amber-500">
            The Analytics Desk
          </p>
          <p className="text-xs text-stone-400 leading-relaxed">
            Director Shepard leads the Institute's sports science and analytics program —
            performance measurement, training science, and data-driven athlete development.
            The desk opens with its launch film:
          </p>
          <div className="rounded-lg overflow-hidden border border-amber-500/30 max-w-xs mx-auto">
            <video
              controls
              playsInline
              preload="metadata"
              data-testid="jalen-launch-film-player"
              className="w-full aspect-[9/16] object-cover bg-black"
            >
              <source src="/assets/directors/jalen-launch-film.mp4" type="video/mp4" />
            </video>
          </div>
          <a
            href="/assets/directors/jalen-launch-film.mp4"
            download="jalen-shepard-launch-film.mp4"
            data-testid="jalen-launch-film-download"
            className="inline-flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 transition-colors"
          >
            Download Jalen's launch film (9:16, ready to post)
          </a>
          <a
            href="/assets/directors/institute-launch-film.mp4"
            download="bgnzn-institute-launch-film.mp4"
            data-testid="institute-launch-film-download"
            className="inline-flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 transition-colors mt-2"
          >
            Download the Institute launch film (9:16, ready to post)
          </a>
        </section>
      </div>
    </div>
  );
};

export default DirectorJalen;
